import { LateChip } from '@/components/entry/LateChip';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { StatusIcon } from '@/components/ui/StatusIcon';
import { format, subDays } from 'date-fns';
import { cn } from '@/lib/cn';
import { useEnteredBy } from '@/hooks/useEnteredBy';
import { clearText } from '@/lib/clearToBottom';
import { formatCalMag, formatPair } from '@/lib/pairs';
import { Table } from 'lucide-react';

interface RecentReadingsTableProps {
  cycleId: string;
  farmId: string;
  /** Closed cycle: 7/30-day ranges count back from today, so only the full cycle applies. */
  closed?: boolean;
}

interface WaterReading {
  id: string;
  reading_date: string;
  reading_slot: string;
  technician_id: string | null;
  created_at: string;
  ph: number | null;
  dissolved_oxygen: number | null;
  temperature_c: number | null;
  ammonia_ppm: number | null;
  salinity_ppt: number | null;
  nitrite_ppm: number | null;
  alkalinity_ppm: number | null;
  alkalinity_am: number | null;
  alkalinity_pm: number | null;
  alkalinity_2?: number | null;
  calcium_ppm?: number | null;
  magnesium_ppm?: number | null;
  depth_m: number | null;
  transparency_cm: number | null;
  transparency_clear_to_bottom?: boolean | null;
  water_color: string | null;
  calmag: string | null;
}

interface ReadingsResponse {
  data: WaterReading[];
  meta: {
    has_more: boolean;
    cursor?: string;
  };
}

// Simple alert detection based on common thresholds
function hasAlert(reading: WaterReading): boolean {
  if (reading.ph !== null && (reading.ph < 7.5 || reading.ph > 8.5)) return true;
  if (reading.dissolved_oxygen !== null && reading.dissolved_oxygen < 4.0) return true;
  if (reading.ammonia_ppm !== null && reading.ammonia_ppm > 0.5) return true;
  if (reading.nitrite_ppm !== null && reading.nitrite_ppm > 0.5) return true;
  return false;
}

const SLOT_ORDER: Record<string, number> = {
  '10pm': 5,
  '7pm': 4,
  '3pm': 3,
  '7am': 2,
  '5am': 1,
};

function sortReadings(readings: WaterReading[]): WaterReading[] {
  return [...readings].sort((a, b) => {
    // Sort by date DESC
    const dateCmp = b.reading_date.localeCompare(a.reading_date);
    if (dateCmp !== 0) return dateCmp;
    // Then by slot order DESC (7pm first)
    return (SLOT_ORDER[b.reading_slot] || 0) - (SLOT_ORDER[a.reading_slot] || 0);
  });
}

type ReadingsRange = '7d' | '30d' | 'cycle';

const RANGE_OPTIONS: { value: ReadingsRange; label: string }[] = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: 'cycle', label: 'Full Cycle' },
];

function getStartDate(range: ReadingsRange): string | undefined {
  if (range === 'cycle') return undefined;
  const days = range === '7d' ? 7 : 30;
  return format(subDays(new Date(), days), 'yyyy-MM-dd');
}

export function RecentReadingsTable({ cycleId, farmId, closed = false }: RecentReadingsTableProps) {
  const [chosenRange, setRange] = useState<ReadingsRange>('7d');
  const range = closed ? 'cycle' : chosenRange;

  const startDate = getStartDate(range);
  const enteredBy = useEnteredBy(farmId);

  const { data, isLoading } = useQuery({
    queryKey: ['readings', cycleId, 'recent', range],
    queryFn: () =>
      api.get<ReadingsResponse>(
        `/api/v1/farms/${farmId}/readings`,
        { params: { cycle_id: cycleId, limit: '100', sort: 'desc', ...(startDate ? { start_date: startDate } : {}) } }
      ),
  });

  const readings = sortReadings(data?.data || []);

  if (isLoading) {
    return (
      <Card>
        <Card.Body className="animate-pulse">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded" />
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      {/* At phone width the range selector wraps under the title; gap-x replaces the action's ml-4 so the wrapped row isn't indented. */}
      <Card.Header
        className="flex-wrap gap-x-4 gap-y-3 [&>div:last-child]:ml-0"
        action={
          closed ? undefined : (
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setRange(opt.value)}
                  className={cn(
                    'px-3 py-1 text-xs font-medium rounded-md transition-all duration-150',
                    range === opt.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )
        }
      >
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-gray-100 rounded-lg">
            <Table className="h-4 w-4 text-gray-600" />
          </div>
          Recent Readings
        </div>
      </Card.Header>

      {/* relative: the header's sr-only label is absolutely positioned and would otherwise escape this scroller and widen the page (#178) */}
      <div className="relative overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Date
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Slot
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                DO
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                pH
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Temp
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Salinity
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Alkalinity
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Ammonia
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Nitrite
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Depth
              </th>
              <th className="text-right py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Transparency
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Color
              </th>
              <th className="text-left py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider whitespace-nowrap">
                Cal/Mag
              </th>
              <th className="text-center py-3 px-4 font-medium text-gray-500 text-xs uppercase tracking-wider w-12">
                <span className="sr-only">Status</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {readings.length === 0 ? (
              <tr>
                <td colSpan={14} className="py-12 text-center text-gray-400">
                  No readings recorded yet
                </td>
              </tr>
            ) : (
              readings.map((reading, idx) => {
                const alert = hasAlert(reading);
                const entered = enteredBy(reading, reading.reading_date);
                return (
                  <tr
                    key={reading.id}
                    className={cn(
                      'hover:bg-gray-50/50 transition-colors',
                      alert && 'bg-status-critical-bg/30'
                    )}
                    style={{
                      animationDelay: `${idx * 20}ms`,
                    }}
                  >
                    <td className="py-3 px-4 font-medium text-gray-900 whitespace-nowrap">
                      {format(new Date(reading.reading_date), 'MMM d, yyyy')}
                    </td>
                    <td className="py-3 px-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs font-medium">
                        {reading.reading_slot}
                      </span>
                      <LateChip row={reading} entryDate={reading.reading_date} />
                      {entered && (
                        <div
                          title="The first person to save this slot. Later edits aren't tracked."
                          className="mt-0.5 max-w-[7rem] break-words text-[11px] leading-tight text-gray-500 md:max-w-none md:whitespace-nowrap"
                        >
                          Started by {entered}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell
                        value={reading.dissolved_oxygen}
                        alert={reading.dissolved_oxygen !== null && reading.dissolved_oxygen < 4.0}
                      />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell
                        value={reading.ph}
                        alert={reading.ph !== null && (reading.ph < 7.5 || reading.ph > 8.5)}
                      />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell value={reading.temperature_c} />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell value={reading.salinity_ppt} />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      {/* alkalinity_ppm since mig 032; am/pm only on older rows (#179); alkalinity_2 is the pair's second number */}
                      <TextCell
                        value={formatPair(
                          reading.alkalinity_ppm ?? reading.alkalinity_am ?? reading.alkalinity_pm,
                          reading.alkalinity_2
                        )}
                      />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell
                        value={reading.ammonia_ppm}
                        alert={reading.ammonia_ppm !== null && reading.ammonia_ppm > 0.5}
                        decimals={2}
                      />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell
                        value={reading.nitrite_ppm}
                        alert={reading.nitrite_ppm !== null && reading.nitrite_ppm > 0.5}
                        decimals={2}
                      />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      <ValueCell value={reading.depth_m} />
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-gray-700">
                      {reading.transparency_clear_to_bottom ? clearText(reading.depth_m) : <ValueCell value={reading.transparency_cm} decimals={0} />}
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      <TextCell value={reading.water_color} />
                    </td>
                    <td className="py-3 px-4 text-gray-700 whitespace-nowrap">
                      <TextCell value={formatCalMag(reading.calcium_ppm, reading.magnesium_ppm, reading.calmag)} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      {alert && (
                        <StatusIcon status="warning" size="sm" showBackground={false} />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {readings.length > 0 && (
        <Card.Footer>
          <p className="text-xs text-gray-400 text-center">
            Showing last {readings.length} {readings.length === 1 ? 'reading' : 'readings'}
          </p>
        </Card.Footer>
      )}
    </Card>
  );
}

function ValueCell({
  value,
  alert,
  decimals = 1,
}: {
  value: number | null;
  alert?: boolean;
  decimals?: number;
}) {
  if (value === null) {
    return <span className="text-gray-300">—</span>;
  }

  return (
    <span
      className={cn(
        alert && 'text-status-critical font-semibold'
      )}
    >
      {typeof value === 'number' ? value.toFixed(decimals) : value}
    </span>
  );
}

function TextCell({ value }: { value: string | null }) {
  if (!value) {
    return <span className="text-gray-300">—</span>;
  }
  // Format snake_case to Title Case
  return <span className="text-sm">{value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>;
}
